import * as _ from 'lodash'
import { Observable, of, throwError } from 'rxjs'
import { catchError, map, switchMap } from 'rxjs/operators'

import { Workspace, DataStoreOptions, SearchQuery } from '@skinsoft/types'
import { fetch } from '@skinsoft/network'
import { EntityDataStore } from '@skinsoft/entity-data-store'
import { bus } from '@skinsoft/bus'

declare const FALLBACK_LOCALE;

const FAIL_FIND_INSTITUTION_ERROR_MESSAGE = "Failed to find institution while changing workspace"

export class WorkspaceDataStore extends EntityDataStore<Workspace> {
    public static getInstance() {
        if (!WorkspaceDataStore.instance) {
            WorkspaceDataStore.instance = new WorkspaceDataStore();
        }

        return WorkspaceDataStore.instance;
    }

    public static install(Vue) {
        Vue.prototype.$workspaceDataStore = WorkspaceDataStore.getInstance();
    }

    private static instance: WorkspaceDataStore = null as any;

    private _currentWorkspace: Workspace
    private _workspaceList: Workspace[] = []

    constructor() {
        super('workspace');
    }

    public update(id: string, body: Partial<Workspace>, endpointOverride?: string, ignoreCachedStatus: boolean = false) {
        return throwError("Cannot update workspace")
    }

    public searchAll(searchQuery: SearchQuery, pathAndValueToCheck?: any, searchEndpointOverride: string | null = null, options: DataStoreOptions = {}) {
        return throwError("Cannot search workspaces")
        // logger
    }

    public searchOneWithText(queryText: string, types: string[]): Promise<Workspace> {
        throw new Error("Cannot search workspaces")
    }

    public searchTitles(types: string[], filterText: string, pageNumber: number = 0, defaultFilter: string = "", size: number = 10, filterIds?: string[], esPath?: any, language: string = FALLBACK_LOCALE) {
        return throwError("Cannot search workspaces")

    }

    public create(createBody?: Partial<Workspace>, body?: Partial<Workspace>): Observable<Workspace> {
        return throwError("Cannot create workspaces")
    }

    public duplicate(entityToDuplicateId: string, createBody?: Partial<Workspace>, body?: Partial<Workspace>) {
        return throwError("Cannot duplicate workspace")
    }

    public delete(id: string) {
        return throwError("Cannot delete workspace")
    }

    public get currentWorkspace() {
        return this._currentWorkspace.id
    }

    public setCurrentWorkspace(id: string): Observable<boolean> {
        const previousWorkspace = this._currentWorkspace
        const nextWorkspace = this.workspaceList.find(workspace => workspace.id === id)
        if (nextWorkspace) {
            this._currentWorkspace = nextWorkspace
            
            return fetch.api(
                '/institution/getAllOtherInstitution',
                { method: 'GET' },
            )
            .pipe(
                switchMap(response => {
                    // by default we consider that the new workspace is on the same institution
                    // for example if response is an empty object it means there is only one institution
                    let newInstitutionId = localStorage.getItem('institution') ?? ''
                    
                    if(response._embedded) {
                        const institutions = _.get(response, '_embedded.institution', null) ?? response._embedded

                        const newWorkspaceInstitution = institutions.find(institution => {
                            return institution.workspaces.find(workspace => workspace.id === id) != undefined
                        })

                        if(newWorkspaceInstitution) {
                            newInstitutionId = newWorkspaceInstitution.id
                            localStorage.setItem(
                                'institution',
                                newWorkspaceInstitution.id,
                            )
                        }
                    }
                    
                    if(!newInstitutionId) {
                        // Logger
                        throw new Error(FAIL_FIND_INSTITUTION_ERROR_MESSAGE)
                    }

                    return fetch.api(
                        `/users/token?institutionId=${newInstitutionId}&workspaceId=${this.currentWorkspace}`,
                        {
                            method: 'GET',
                        },
                    )
                }),
                catchError(err => {
                    this._currentWorkspace = previousWorkspace
                    return of(false)
                }),
                map(response => {
                    if(response) {
                        localStorage.setItem('token', response.token)
                        fetch.registerToken(response.token)
                        localStorage.setItem('workspace', this.currentWorkspace)
                        bus.emit('change_current_workspace', this.currentWorkspace)
                        return true
                    }
                    this._currentWorkspace = previousWorkspace
                    return false
                })
            )
        }

        return of(false)
    }

    public get currentWorkspaceInfo() {
        return _.cloneDeep(this._currentWorkspace)
    }

    public get workspaceList(): Workspace[] {
        return _.cloneDeep(this._workspaceList) ?? []
    }

    public set workspaceList(workspaceList: Workspace[]) {
        this._workspaceList = _.cloneDeep(workspaceList)
    }
}
